<template>
    <div
        ref="radioGroupRef"
        role="radiogroup"
        class="flex flex-col gap-3"
        data-test="radio-group"
        @focusout="leaveGroup($event)"
    >
        <div
            v-for="(option, index) in props.options"
            :key="option.value"
            class="flex gap-2 items-center text-xs cursor-pointer"
            @click.prevent="select(index, { focusVisible: false })"
        >
            <vx-radio
                role="radio"
                :aria-checked="props.modelValue === option.value"
                :aria-labelledby="`${labelId}-${index}`"
                :tabindex="index === tabStop ? 0 : -1"
                :data-test="`${props.name}-${option.value}`"
                :modelValue="props.modelValue === option.value"
                @focusin="focused = index"
                @keydown.space.prevent="emit('update:modelValue', option.value)"
                @keydown.enter.prevent="emit('update:modelValue', option.value)"
                @keydown.down.exact.prevent="move(index, 1)"
                @keydown.right.exact.prevent="move(index, 1)"
                @keydown.up.exact.prevent="move(index, -1)"
                @keydown.left.exact.prevent="move(index, -1)"
            ></vx-radio>
            <span :id="`${labelId}-${index}`">{{ option.label }}</span>
        </div>
    </div>
</template>

<script lang="ts" setup generic="T extends string">
import { VxRadio } from '@voxie/frontend-components';
import { computed, ref, useId, useTemplateRef } from 'vue';

const props = defineProps<{
    name: string;
    options: readonly { label: string; value: T; opensPicker?: boolean }[];
    modelValue?: T;
}>();

const emit = defineEmits<{
    'update:modelValue': [value: T];
}>();

const labelId = useId();
const radioGroupRef = useTemplateRef<HTMLElement>('radioGroupRef');

// Arrows only focus an option that opens a picker, so the tab stop follows focus.
const focused = ref<number>();

const tabStop = computed(() => {
    if (undefined !== focused.value) {
        return focused.value;
    }

    const checked = props.options.findIndex((option) => option.value === props.modelValue);

    return -1 === checked ? 0 : checked;
});

const leaveGroup = (event: FocusEvent) => {
    if (!radioGroupRef.value?.contains(event.relatedTarget as Node | null)) {
        focused.value = undefined;
    }
};

const focus = (index: number, options?: FocusOptions) => {
    radioGroupRef.value?.querySelectorAll<HTMLElement>('[role="radio"]')[index]?.focus(options);
};

const select = (index: number, options?: FocusOptions) => {
    const option = props.options[index];

    if (option) {
        emit('update:modelValue', option.value);
    }

    focus(index, options);
};

const move = (index: number, step: number) => {
    const next = (index + step + props.options.length) % props.options.length;

    if (props.options[next]?.opensPicker) {
        focus(next);
    } else {
        select(next);
    }
};
</script>
